/**
 * useResolvedIssueEvents - live resolved-print-issue updates over SSE.
 *
 * Opens an EventSource to `/events/print-files`. When the server pushes a
 * `print_file_issue_resolved` or `print_file_healed` event for the currently
 * selected location, the print-station "recently resolved" feed query is
 * invalidated so the retry panel refreshes live (a newly-resolved garment
 * appears without the operator hitting refresh).
 *
 * Copied from `hooks/usePrintFileEvents.ts` — transport/backoff/token-refresh
 * are identical:
 *  - manual exponential-backoff reconnect (NOT browser-native, which storms
 *    when offline),
 *  - recreates the EventSource on Supabase auth-token refresh,
 *  - fail-closed cleanup on unmount.
 *
 * This hook has no return value — its only job is cache invalidation. Mount it
 * once on the retry surface (e.g. ResolvedIssuesPanel) for the active location.
 * The original `usePrintFileEvents` (issues-list surface) is left untouched.
 */

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trpc } from '@/lib/trpc';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:59321';
const RECONNECT_BASE_MS = 3_000; // Initial retry delay
const RECONNECT_MAX_MS = 60_000; // Max retry delay (1 minute)

type PrintFileEvent = {
  type:
    | 'print_file_healed'
    | 'print_file_issue_created'
    | 'print_file_issue_resolved'
    | 'heartbeat';
  organizationId?: string;
  locationId?: string;
  batchId?: string;
  prodOrderId?: string;
  sku?: string;
  issueId?: string;
  shopifySku?: string;
  resolvedAt?: string;
};

/**
 * @param locationId The active location. Events for other locations are
 *   ignored. When falsy, no connection is opened.
 */
export function useResolvedIssueEvents(locationId: string | null | undefined): void {
  const utils = trpc.useUtils();

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastTokenRef = useRef<string>('');
  const mountedRef = useRef(true);
  // Keep the latest locationId in a ref so the long-lived EventSource handler
  // always filters against the current location without re-opening the socket.
  const locationIdRef = useRef<string | null | undefined>(locationId);
  locationIdRef.current = locationId;

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const createEventSource = useCallback(
    (token: string) => {
      // Close existing connection and cancel pending reconnect.
      clearReconnectTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      if (!token) return;
      lastTokenRef.current = token;

      const es = new EventSource(
        `${BACKEND_URL}/events/print-files?token=${encodeURIComponent(token)}`
      );

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as PrintFileEvent;
          reconnectAttemptRef.current = 0; // Reset backoff on any valid message.
          if (data.type === 'heartbeat') return;
          // Only these two affect the resolved feed: a genuine resolve, and a
          // self-heal that flips a missing file back to ready.
          if (
            data.type !== 'print_file_issue_resolved' &&
            data.type !== 'print_file_healed'
          ) {
            return;
          }
          // Only refresh when the event is for the location we're viewing.
          const active = locationIdRef.current;
          if (!active || data.locationId !== active) return;
          void utils.printStation.getRecentlyResolvedIssues.invalidate();
        } catch {
          // Ignore malformed messages.
        }
      };

      es.onerror = () => {
        // Close immediately to prevent the browser's native reconnect storm.
        es.close();
        eventSourceRef.current = null;

        if (!mountedRef.current) return;
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(
          RECONNECT_BASE_MS * Math.pow(2, attempt),
          RECONNECT_MAX_MS
        );
        reconnectAttemptRef.current = attempt + 1;

        reconnectTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          if (lastTokenRef.current) {
            createEventSource(lastTokenRef.current);
          }
        }, delay);
      };

      eventSourceRef.current = es;
    },
    [clearReconnectTimer, utils]
  );

  useEffect(() => {
    mountedRef.current = true;

    // No location → no stream.
    if (!locationId) {
      return () => {
        mountedRef.current = false;
      };
    }

    // Get the initial session token, then open the stream.
    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mountedRef.current || !session?.access_token) return;
      createEventSource(session.access_token);
    });

    // Recreate the EventSource on token refresh (JWT expiry).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mountedRef.current) return;
      if (session?.access_token) {
        reconnectAttemptRef.current = 0;
        createEventSource(session.access_token);
      } else {
        clearReconnectTimer();
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      }
    });

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      clearReconnectTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
    // locationId in deps: re-open the stream when the active location changes
    // (token + handler use refs, so they don't need to be deps).
  }, [locationId, createEventSource, clearReconnectTimer]);
}
